/**
 * Favorite Toggle Component
 *
 * Reusable heart icon button for favoriting guides and experiences.
 * Works for both authenticated and non-authenticated users.
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-15: Favorite Toggle (2 SP)
 *
 * Props:
 * - itemType: 'guide' or 'experience'
 * - itemId: UUID of the guide or experience
 * - isFavorited: boolean indicating current favorite status
 * - onToggle: callback function(newStatus) called after successful toggle
 * - size: 'sm', 'md', 'lg' (optional, default 'md')
 * - className: additional CSS classes (optional)
 *
 * API Endpoints:
 * - POST /api/v1/customer/favorites/:type/:id (add favorite)
 * - DELETE /api/v1/customer/favorites/:type/:id (remove favorite)
 */

import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import apiRequest from '../../services/api';
import * as authService from '../../services/authService';
import { logError } from '../../services/errorLoggingService';

export default function FavoriteToggle({
  itemType,
  itemId,
  isFavorited = false,
  onToggle,
  size = 'md',
  className = ''
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [favorited, setFavorited] = useState(isFavorited);
  const [errorMessage, setErrorMessage] = useState(null);

  // Size mappings
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-2.5'
  };

  const handleToggle = async (  _e) => {
    _e.preventDefault();
    _e.stopPropagation();

    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      // Redirect to login with return URL
      const currentUrl = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
      return;
    }

    try {
      setIsLoading(true);

      // Determine API endpoint based on action
      const endpoint = `/api/v1/customer/favorites/${itemType}s/${itemId}`;

      if (favorited) {
        // Remove from favorites
        await apiRequest(endpoint, { method: 'DELETE' });
        setFavorited(false);
        if (onToggle) {onToggle(false);}
      } else {
        // Add to favorites
        await apiRequest(endpoint, { method: 'POST' });
        setFavorited(true);
        if (onToggle) {onToggle(true);}
      }
    } catch (error) {
      logError(error, {
        componentName: 'FavoriteToggle',
        action: favorited ? 'remove_favorite' : 'add_favorite',
        itemType,
        itemId
      });

      // Handle authentication errors
      if (error.response?.status === 401) {
        const currentUrl = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
        return;
      }

      // Set error message for display
      setErrorMessage(
        favorited
          ? 'Failed to remove from favorites. Please try again.'
          : 'Failed to add to favorites. Please try again.'
      );

      // Clear error after 5 seconds
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          inline-flex items-center justify-center
          rounded-full
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
          ${/* eslint-disable-next-line security/detect-object-injection -- safe: size is controlled prop enum */ buttonSizeClasses[size]}
          ${
            favorited
              ? 'text-red-500 hover:text-red-600 hover:scale-110'
              : 'text-gray-400 hover:text-red-500 hover:scale-110'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `}
        title={favorited ? 'Remove from favorites' : 'Add to favorites'}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        {isLoading ? (
          <div
            // eslint-disable-next-line security/detect-object-injection -- safe: size is controlled prop enum
            className={`animate-spin rounded-full border-b-2 border-current ${sizeClasses[size]}`}
          />
        ) : (
          <Heart
            // eslint-disable-next-line security/detect-object-injection -- safe: size is controlled prop enum
            className={`${sizeClasses[size]} ${favorited ? 'fill-current' : ''}`}
          />
        )}
      </button>

      {/* Error message display */}
      {errorMessage && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-48 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm z-10">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
